/**
 * Single source of truth for all email template placeholders.
 *
 * Every placeholder listed here is handled by `replacePlaceholders()` in templateUtils.ts.
 * Add new placeholders here first, then wire them in templateUtils + buildPlaceholderData.
 */

export type PlaceholderDef = {
  placeholder: string;   // Exact syntax to use in templates e.g. {{Entity Name}}
  label: string;         // Human-readable name
  description: string;   // Where the value comes from
  category: 'Client' | 'Contract' | 'Usage' | 'Solar' | 'Other';
  autoFilled: boolean;   // true = replaced automatically from Step 1 data; false = edit manually
};

export const EMAIL_PLACEHOLDERS: PlaceholderDef[] = [
  // ── Client Details ─────────────────────────────────────────────────────────
  {
    placeholder: '{{Retailer Name}}',
    label: 'Retailer Name',
    description: 'Name of the selected retailer',
    category: 'Client',
    autoFilled: true,
  },
  {
    placeholder: '{{Entity Name}}',
    label: 'Entity Name',
    description: 'Client legal entity name (Step 1)',
    category: 'Client',
    autoFilled: true,
  },
  {
    placeholder: '{{ABN}}',
    label: 'ABN',
    description: 'Australian Business Number (Step 1)',
    category: 'Client',
    autoFilled: true,
  },
  {
    placeholder: '{{ACN}}',
    label: 'ACN',
    description: 'Australian Company Number (Step 1)',
    category: 'Client',
    autoFilled: true,
  },
  {
    placeholder: '{{NMI}}',
    label: 'NMI',
    description: 'National Metering Identifier — first site (Step 1)',
    category: 'Client',
    autoFilled: true,
  },
  {
    placeholder: '{{Address}}',
    label: 'Address',
    description: 'Site address — first site (Step 1)',
    category: 'Client',
    autoFilled: true,
  },

  // ── Contract Requirements ───────────────────────────────────────────────────
  {
    placeholder: '{{Start Date}}',
    label: 'Start Date',
    description: 'Contract start date — current contract expiry (Step 1)',
    category: 'Contract',
    autoFilled: true,
  },
  {
    placeholder: '{{Contract Terms}}',
    label: 'Contract Terms',
    description: 'Requested contract terms e.g. "12, 24, 36 & 48 Month"',
    category: 'Contract',
    autoFilled: true,
  },
  {
    placeholder: '{{Pricing Due Date}}',
    label: 'Pricing Due Date',
    description: 'Pricing deadline (Step 1 > Due At)',
    category: 'Contract',
    autoFilled: true,
  },

  // ── Annual Usage ────────────────────────────────────────────────────────────
  {
    placeholder: '{{Peak kWh}}',
    label: 'Peak kWh',
    description: 'Annual peak consumption — first site (Step 1)',
    category: 'Usage',
    autoFilled: true,
  },
  {
    placeholder: '{{Off Peak kWh}}',
    label: 'Off Peak kWh',
    description: 'Annual off-peak consumption — first site (Step 1)',
    category: 'Usage',
    autoFilled: true,
  },
  {
    placeholder: '{{Shoulder kWh}}',
    label: 'Shoulder kWh',
    description: 'Annual shoulder consumption — first site (Step 1)',
    category: 'Usage',
    autoFilled: true,
  },
  {
    placeholder: '{{Total kWh}}',
    label: 'Total kWh',
    description: 'Total annual consumption — first site (Step 1)',
    category: 'Usage',
    autoFilled: true,
  },

  // ── Other Details ───────────────────────────────────────────────────────────
  {
    placeholder: '{{Comms}}',
    label: 'Comms',
    description: 'Retailer commission / comms rate (Step 1)',
    category: 'Other',
    autoFilled: true,
  },
  {
    placeholder: '{{DMA}}',
    label: 'DMA',
    description: 'DMA provider (Step 1)',
    category: 'Other',
    autoFilled: true,
  },
  {
    placeholder: '{{Special Requests}}',
    label: 'Special Requests',
    description: 'Special notes or requirements',
    category: 'Other',
    autoFilled: false,
  },
  {
    placeholder: '{{Organization Name}}',
    label: 'Organization Name',
    description: 'Your organisation name',
    category: 'Other',
    autoFilled: true,
  },

  // ── Solar / Feed-in ─────────────────────────────────────────────────────────
  {
    placeholder: '{{Solar Capacity}}',
    label: 'Solar Capacity',
    description: 'Solar system capacity e.g. "100kW solar" — edit manually',
    category: 'Solar',
    autoFilled: false,
  },
  {
    placeholder: '{{Estimated Generation}}',
    label: 'Estimated Generation',
    description: 'Estimated annual solar generation e.g. "154,000kWh" — edit manually',
    category: 'Solar',
    autoFilled: false,
  },
  {
    placeholder: '{{Estimated Feed In}}',
    label: 'Estimated Feed In',
    description: 'Estimated feed-in to grid e.g. "1,000 kWh" — edit manually',
    category: 'Solar',
    autoFilled: false,
  },
];

export const PLACEHOLDER_CATEGORIES = ['Client', 'Contract', 'Usage', 'Other', 'Solar'] as const;
